// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import CheckoutShippingState from "@insite/client-framework/Store/Pages/CheckoutShipping/CheckoutShippingState";
import { reducers } from "@insite/client-framework/Store/Reducers";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";

interface OnePageCheckoutShippingState
    extends Pick<
        CheckoutShippingState,
        "useOneTimeAddress" | "oneTimeAddressState" | "useBillingAddress" | "lastSelectedShippingAddress"
    > {
    isBillingAddressEditing?: boolean;
    isShippingAddressEditing?: boolean;
    isSubmitting?: boolean;
    isAssociatedSalesRepModalOpen?: boolean;
    isUpdatingCheckout?: boolean;
}

const initialState: OnePageCheckoutShippingState = {
    useBillingAddress: false,
    useOneTimeAddress: false,
    isBillingAddressEditing: false,
    isShippingAddressEditing: false,
    lastSelectedShippingAddress: {} as ShipToModel,
    isSubmitting: false,
    isAssociatedSalesRepModalOpen: true,
    isUpdatingCheckout: true,
};

const reducer = {
    "xOnePageCheckout/SetUseBillingAddress": (
        draft: Draft<OnePageCheckoutShippingState>,
        { useBillingAddress }: { useBillingAddress: boolean },
    ) => {
        draft.useBillingAddress = useBillingAddress;
        draft.useOneTimeAddress = false;
    },
    "xOnePageCheckout/SetUseOneTimeAddress": (
        draft: Draft<OnePageCheckoutShippingState>,
        { useOneTimeAddress }: { useOneTimeAddress: boolean },
    ) => {
        draft.useOneTimeAddress = useOneTimeAddress;
        draft.useBillingAddress = false;
    },
    "xOnePageCheckout/SetLastSelectedShipTo": (
        draft: Draft<OnePageCheckoutShippingState>,
        action: { shipTo: ShipToModel },
    ) => {
        draft.lastSelectedShippingAddress = action.shipTo;
        draft.useBillingAddress = false;
    },
    "xOnePageCheckout/IsBillingAddressEditing": (
        draft: Draft<OnePageCheckoutShippingState>,
        action: { isEditing: boolean },
    ) => {
        draft.isBillingAddressEditing = action.isEditing;
    },
    "xOnePageCheckout/IsShippingAddressEditing": (
        draft: Draft<OnePageCheckoutShippingState>,
        action: { isEditing: boolean },
    ) => {
        draft.isShippingAddressEditing = action.isEditing;
    },
    "xOnePageCheckout/IsSubmitting": (
        draft: Draft<OnePageCheckoutShippingState>,
        action: { isSubmitting: boolean },
    ) => {
        draft.isSubmitting = action.isSubmitting;
    },
    "xOnePageCheckout/setIsAssociatedSalesRepModalOpen": (
        draft: Draft<OnePageCheckoutShippingState>,
        { isOpen }: { isOpen: boolean },
    ) => {
        draft.isAssociatedSalesRepModalOpen = isOpen;
    },
    "xOnePageCheckout/setIsUpdatingCheckout": (
        draft: Draft<OnePageCheckoutShippingState>,
        { isUpdatingCheckout }: { isUpdatingCheckout: boolean },
    ) => {
        draft.isUpdatingCheckout = isUpdatingCheckout;
    },
    "xOnePageCheckout/Reset": (draft: Draft<OnePageCheckoutShippingState>) => {
        return { ...initialState };
    },
};

const xOnePageCheckout = createTypedReducerWithImmer(initialState, reducer);
(reducers as any).xOnePageCheckout = xOnePageCheckout;

export type xOnePageCheckoutState = {
    xOnePageCheckout: OnePageCheckoutShippingState;
};
